import { DataBlock } from "~/components/data-block";
import { RecentTransactions, Transaction } from "./recent-transactions";
import { getIconUrl, onIconError, prettyValue, roundALR } from "~/lib/util";
import { LiquidityPositionRow, PositionData } from "../liquidity-position-row";

type InfoPanelProps = {
  pair: any;
  baseToken: any;
  quoteToken: any;
  transactions: Transaction[];
  basePoolAmounts?: { base: number; quote: number };
  quotePoolAmounts?: { base: number; quote: number };
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  userPositions?: PositionData[];
  isIncentivized?: boolean;
};

export function InfoPanel({ pair, baseToken, quoteToken, transactions, basePoolAmounts, quotePoolAmounts, onLoadMore, hasMore, isLoadingMore, userPositions, isIncentivized }: InfoPanelProps) {
  return (
    <div className="flex flex-col space-y-4 lg:w-1/2">
      <h2 className="flex items-center text-3xl font-bold text-gray-900 dark:text-white">
        <div className="relative mr-4 h-8 w-12">
          <img className="absolute left-4 top-0 h-8 w-8 shrink-0 rounded-full bg-white dark:bg-white/[0.04]" src={getIconUrl(quoteToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
          <img className="absolute left-0 top-0 h-8 w-8 shrink-0 rounded-full bg-white dark:bg-white/[0.04]" src={getIconUrl(baseToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
        </div>
        {baseToken.symbol} / {quoteToken.symbol}
        {isIncentivized ? <span className="ml-3 rounded bg-black/10 p-1 text-xs text-black/50 dark:bg-white/10 dark:text-white/50" title="This pool is incentivized by the Radix Rewards program.">RR</span> : null}
      </h2>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DataBlock title="Liquidity" data={`$${prettyValue(pair.tvlUSD)}`} />
        <DataBlock title="Fee" data={`${(pair.config?.fee * 100).toFixed(2)}%`} />

        <div className="dark:bg-background-900 flex grow flex-row items-center justify-between rounded-xl bg-white px-4 py-2 lg:flex-col lg:items-start lg:justify-normal lg:space-y-4 lg:rounded-2xl lg:p-8">
          <dt className="text-primary-800 dark:text-primary-100 truncate font-medium lg:text-sm">Base Liquidity</dt>
          <dd className="mt-1 tracking-tight text-gray-900 dark:text-white">
            <div className="text-3xl font-semibold">{prettyValue(basePoolAmounts?.base || 0)} {baseToken.symbol}</div>
            <div className="text-lg">{prettyValue(basePoolAmounts?.quote || 0)} {quoteToken.symbol}</div>
          </dd>
        </div>

        <div className="dark:bg-background-900 flex grow flex-row items-center justify-between rounded-xl bg-white px-4 py-2 lg:flex-col lg:items-start lg:justify-normal lg:space-y-4 lg:rounded-2xl lg:p-8">
          <dt className="text-primary-800 dark:text-primary-100 truncate font-medium lg:text-sm">Quote Liquidity</dt>
          <dd className="mt-1 tracking-tight text-gray-900 dark:text-white">
            <div className="text-3xl font-semibold">{prettyValue(quotePoolAmounts?.quote || 0)} {quoteToken.symbol}</div>
            <div className="text-lg">{prettyValue(quotePoolAmounts?.base || 0)} {baseToken.symbol}</div>
          </dd>
        </div>

        <div className="dark:bg-background-900 flex grow flex-row items-center justify-between rounded-xl bg-white px-4 py-2 lg:flex-col lg:items-start lg:justify-normal lg:space-y-4 lg:rounded-2xl lg:p-8">
          <dt className="text-primary-800 dark:text-primary-100 truncate font-medium lg:text-sm">Base ALR</dt>
          <dd className="mt-1 tracking-tight text-gray-900 dark:text-white">
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-semibold">{roundALR(pair.baseAPY7D)}% 7D</span>
            </div>
            <div className="flex items-baseline space-x-2">
              <span className="text-lg">{roundALR(pair.baseAPY)}% 1Y</span>
            </div>
          </dd>
        </div>

        <div className="dark:bg-background-900 flex grow flex-row items-center justify-between rounded-xl bg-white px-4 py-2 lg:flex-col lg:items-start lg:justify-normal lg:space-y-4 lg:rounded-2xl lg:p-8">
          <dt className="text-primary-800 dark:text-primary-100 truncate font-medium lg:text-sm">Quote ALR</dt>
          <dd className="mt-1 tracking-tight text-gray-900 dark:text-white">
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-semibold">{roundALR(pair.quoteAPY7D)}% 7D</span>
            </div>
            <div className="flex items-baseline space-x-2">
              <span className="text-lg">{roundALR(pair.quoteAPY)}% 1Y</span>
            </div>
          </dd>
        </div>
      </dl>

      {userPositions && userPositions.length > 0 ? (
        <div className="rounded-xl bg-white p-4 dark:bg-background-900 lg:p-6">
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Your Positions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="pl-0 pr-3 text-left text-sm font-semibold lg:py-3.5 lg:pl-4">
                    Pair
                  </th>
                  <th colSpan={2} scope="col" className="hidden px-3 pr-1 text-left text-sm font-semibold lg:table-cell lg:py-3.5">
                    Tokens
                  </th>
                  <th scope="col" className="hidden px-3 pr-1 text-left text-sm font-semibold lg:table-cell lg:py-3.5">
                    Share
                  </th>
                  <th scope="col" className="relative pl-3 pr-0 lg:py-3.5 lg:pr-4"></th>
                </tr>
              </thead>
              <tbody className="divide-primary-200 lg:divide-y">
                {userPositions.map((position) => (
                  <LiquidityPositionRow key={position.lpToken.address} row={position} showALR={false} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl bg-white p-4 dark:bg-background-900 lg:p-6">
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Recent Transactions</h3>
        <RecentTransactions transactions={transactions} baseTokenSymbol={baseToken.symbol} quoteTokenSymbol={quoteToken.symbol} />

        {hasMore ? (
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="mt-4 w-full rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
